// src/components/ui/sections/BusinessSettingsEditor/BusinessSettingsEditorShadcn.jsx
import React from 'react';
import {
  Building2,
  Clock,
  Settings,
  Mail,
  Bell,
  CreditCard,
  Plug2
} from 'lucide-react';

// shadcn components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../card';

// Import shadcn section components
import CompanyInformationSectionShadcn from '../CompanyInformationSection/CompanyInformationSectionShadcn';
import BusinessHoursSectionShadcn from '../BusinessHoursSection/BusinessHoursSectionShadcn';
import OperationalSettingsSectionShadcn from '../OperationalSettingsSection/OperationalSettingsSectionShadcn';
import EmailConfigurationSectionShadcn from '../EmailConfigurationSection/EmailConfigurationSectionShadcn';
import BusinessNotificationsSectionShadcn from '../BusinessNotificationsSection/BusinessNotificationsSectionShadcn';
import PaymentSettingsSectionShadcn from '../PaymentSettingsSection/PaymentSettingsSectionShadcn';
import GBPIntegrationsSectionShadcn from '../GBPIntegrationsSection/GBPIntegrationsSectionShadcn';

import { cn } from '../../../../utils/cn';

const BusinessSettingsEditorShadcn = ({
  selectedSection,
  // Company form props
  companyForm,
  setCompanyForm,
  handleCompanySubmit,
  // Operational form props
  operationalForm,
  setOperationalForm,
  handleOperationalSubmit,
  // Hours form props
  hoursForm,
  handleHoursSubmit,
  handleHourChange,
  // Email form props
  emailForm,
  setEmailForm,
  handleEmailSubmit,
  // State flags
  savingSettings,
  savingHours,
  savingEmailSettings,
  // Change detection flags
  hasCompanyChanges,
  hasOperationalChanges,
  hasHoursChanges,
  hasEmailChanges,
  // Utility data
  daysOfWeek,
  stateOptions,
  paymentTermsOptions,
  className = ''
}) => {
  // Section configuration for header display
  const sectionConfig = {
    company: {
      title: 'Company Information',
      description: 'Basic information about your business',
      icon: Building2
    },
    hours: {
      title: 'Business Hours',
      description: 'Set your operating hours for each day of the week',
      icon: Clock
    },
    operational: {
      title: 'Operational Settings',
      description: 'Configure scheduling and business rules',
      icon: Settings
    },
    email: {
      title: 'Email Configuration',
      description: 'Configure how your business sends emails to customers',
      icon: Mail
    },
    notifications: {
      title: 'Business Notifications',
      description: 'Get notified when appointments are scheduled, rescheduled, or cancelled',
      icon: Bell
    },
    payments: {
      title: 'Payment Processing',
      description: 'Connect and manage Stripe payment processing',
      icon: CreditCard
    },
    integrations: {
      title: 'Integrations',
      description: 'Connect to Google Business Profile and other external services',
      icon: Plug2
    }
  };

  const currentSection = sectionConfig[selectedSection];
  const Icon = currentSection?.icon || Settings;

  // Render the appropriate section content
  const renderSectionContent = () => {
    switch (selectedSection) {
      case 'company':
        return (
          <CompanyInformationSectionShadcn
            companyForm={companyForm}
            setCompanyForm={setCompanyForm}
            handleCompanySubmit={handleCompanySubmit}
            savingSettings={savingSettings}
            hasChanges={hasCompanyChanges}
            stateOptions={stateOptions}
          />
        );

      case 'hours':
        return (
          <BusinessHoursSectionShadcn
            hoursForm={hoursForm}
            handleHoursSubmit={handleHoursSubmit}
            handleHourChange={handleHourChange}
            savingHours={savingHours}
            hasChanges={hasHoursChanges}
            daysOfWeek={daysOfWeek}
          />
        );

      case 'operational':
        return (
          <OperationalSettingsSectionShadcn
            operationalForm={operationalForm}
            setOperationalForm={setOperationalForm}
            handleOperationalSubmit={handleOperationalSubmit}
            savingSettings={savingSettings}
            hasChanges={hasOperationalChanges}
            paymentTermsOptions={paymentTermsOptions}
          />
        );

      case 'email':
        return (
          <EmailConfigurationSectionShadcn
            emailForm={emailForm}
            setEmailForm={setEmailForm}
            handleEmailSubmit={handleEmailSubmit}
            savingEmailSettings={savingEmailSettings}
            hasChanges={hasEmailChanges}
          />
        );

      case 'notifications':
        return <BusinessNotificationsSectionShadcn />;

      case 'payments':
        return <PaymentSettingsSectionShadcn />;

      case 'integrations':
        return <GBPIntegrationsSectionShadcn />;

      default:
        return (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Settings className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Select a settings section to configure</p>
            </div>
          </div>
        );
    }
  };

  return (
    <Card className={cn('h-full flex flex-col', className)}>
      {/* Header */}
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
            <Icon className="w-4 h-4 text-muted-foreground" />
          </div>
          <div>
            <CardTitle>
              {currentSection?.title || 'Settings'}
            </CardTitle>
            {currentSection?.description && (
              <CardDescription className="mt-1">
                {currentSection.description}
              </CardDescription>
            )}
          </div>
        </div>
      </CardHeader>

      {/* Content Area - Scrollable */}
      <CardContent className="flex-1 min-h-0 overflow-y-auto p-0">
        {renderSectionContent()}
      </CardContent>
    </Card>
  );
};

export default BusinessSettingsEditorShadcn;